import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Gift, Menu, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAdmin } from '@/hooks/useAdmin';
import { cn } from '@/lib/utils';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAdmin();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/', label: 'Startseite' },
    { href: '/geschenkideen', label: 'Geschenkideen' },
    { href: '/produkte', label: 'Produkte' },
    { href: '/magazin', label: 'Magazin' },
    { href: '/ueber-uns', label: 'Über uns' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header 
      className={cn(
        "sticky top-0 z-40 w-full transition-all duration-300",
        scrolled 
          ? "bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-sm" 
          : "bg-transparent border-b border-transparent"
      )}
    >
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link 
          to="/" 
          className="flex items-center gap-2.5 group"
        >
          <div className={cn(
            "p-2 rounded-xl transition-all duration-300 group-hover:scale-110",
            scrolled ? "bg-primary/10" : "bg-primary/5"
          )}>
            <Gift className="w-5 h-5 text-primary transition-transform duration-300 group-hover:rotate-12" />
          </div>
          <span className="text-xl font-bold tracking-tight">Budgetter</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
          {navLinks.map((link) => (
            <Link 
              key={link.href}
              to={link.href} 
              className={cn(
                "px-4 py-2 rounded-full text-sm font-medium transition-all duration-200",
                isActive(link.href)
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              )}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          {isAdmin && (
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => navigate('/admin/dashboard')}
            >
              Admin
            </Button>
          )}
          <Button 
            size="sm"
            variant="hero"
            onClick={() => navigate('/questionnaire')}
            className="gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Geschenk finden
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          className={cn(
            "md:hidden p-2.5 rounded-xl transition-all duration-200 min-h-[44px] min-w-[44px]",
            mobileMenuOpen 
              ? "bg-secondary text-foreground" 
              : "hover:bg-secondary/50"
          )}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? "Menü schließen" : "Menü öffnen"}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div 
            id="mobile-menu" 
            className="absolute top-16 left-0 right-0 bg-background/98 backdrop-blur-xl border-b border-border/50 md:hidden animate-fade-in"
          >
            <nav className="container mx-auto px-4 py-6 flex flex-col gap-2" aria-label="Mobile Navigation">
              {navLinks.map((link) => (
                <Link 
                  key={link.href}
                  to={link.href} 
                  className={cn(
                    "px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200",
                    isActive(link.href)
                      ? "bg-primary/15 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-border/50 my-3" />
              {isAdmin && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    navigate('/admin/dashboard');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full justify-center"
                >
                  Admin
                </Button>
              )}
              <Button 
                variant="hero"
                onClick={() => {
                  navigate('/questionnaire');
                  setMobileMenuOpen(false);
                }}
                className="w-full justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Geschenk finden
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};